import React, { useEffect, useState, useRef } from 'react';
import PropTypes from 'prop-types';
import s from './ModelSetpoints.module.scss';
import { FormattedMessage, injectIntl } from 'react-intl';
import LoadingSpinner from '../LoadingSpinner/LoadingSpinner';
import Banner from '../Banner/Banner';
import { initialModelState } from '../../reducers/initialState';
import ModelSetpointsListTable from './ModelSetpointsListTable';
import { WidgetVisibility, IsWidgetAccessible } from '../../utils/widgetManager';
import SearchBar from '../SearchBar/SearchBar';
import Button from 'react-bootstrap/Button';
import Row from 'react-bootstrap/Row';
import Col from 'react-bootstrap/Col';
import Cookies from 'js-cookie';
import ConfirmDialog from '../ConfirmDialog/ConfirmDialog';
import { getModelCategoryPermission , sortSetpoints} from '../../utils';
import { useParams } from 'react-router-dom';
import { DEFAULT_PAGE_SIZE, ModelCategoryPermission } from '../../constants';
import Breadcrumb from '../WSAControls/Breadcrumb/BreadCrumb';
import ModelConfigurationContentTemplate from '../ModelTab/ModelConfigurationContentTemplate';
import Pagination from '../Pagination/Pagination';


const ModelSetpointsList = ({
  modelSetpoints = { ...initialModelState.modelManager.selectedModel.ModelSetpoints },
  intl,
  userProfileWidgets,
  getModelSetpoints,
  saveModelSetpoint,
  describeModelSetpoint,
  changeModelSetpoint,
  setModelSetpointShowScreenStatus,
  userModelCategories,
  modelCategorySetpoints,
  setCurrentPage,
  setFilterData,
  currentTimezone
}) => {
  let filter = '';
  let offset = 0;
  let limit = 1000;

  let { ModelCategoryId, ModelId } = useParams();

  useEffect(() => {
    getModelSetpoints(ModelCategoryId, ModelId);
  }, [getModelSetpoints, ModelCategoryId, ModelId]);

  const [currentEditId, setCurrentEditId] = useState(-1);
  const [active, setActive] = useState(false);
  const [delModelSetpointId, setModelSetpointId] = useState(0);
  const [searchText, setSearchText] = useState('');
  const [ReadOnly, setReadonly] = useState(false);

  const envName = process.env.REACT_APP_ENV_NAME_SHORT;

  const addNewRef = useRef(null);

  const onPageChangeHandler = currentPage => {
    setCurrentPage(currentPage);
  };


  const searchHandler = value => {
    setCurrentPage(1);
    setSearchText(value);
  };
  const disableModeHandler = status => {
    setModelSetpointShowScreenStatus(status);
  };



  const setAddHandler = () => {
    setModelSetpointShowScreenStatus(true);
    setCurrentEditId(0);
    window.scrollTo(0, addNewRef.current.offsetTop);
  };

  const setEditHandler = modelSetpointId => {
    setCurrentEditId(modelSetpointId);
    if (modelSetpointId === -1) {
      changeModelSetpoint({
        SelectedModelSetpoint: {
          ...initialModelState.modelManager.selectedModel.ModelSetpoints.SelectedModelSetpoint,
          ModelId: ModelId
        }
      });
      setModelSetpointShowScreenStatus(false);
    } else {
      setModelSetpointShowScreenStatus(true);
    }
  };

  const filterData = () => {
    let filteredList = modelSetpoints.modelSetpointList;

    filteredList = modelSetpoints.modelSetpointList.filter(function (item) {
      return item.Name.toLowerCase().includes(searchText.toLowerCase());
    });


    return filteredList;
  };



  const removeModelSetpointClick = modelSetpointId => {
    setActive(true);
    setModelSetpointId(modelSetpointId);
  };

  const DeleteModelSetpoint = () => {
    let saveData = modelSetpoints.selectedModelSetpoint;
    saveData.MarkAsDeleted = true;
    saveData.ModelId = ModelId;
    saveData.UpdatedBy = Cookies.get(`userid-${envName}`) || 0;
    saveData.ModelSetpointId = delModelSetpointId;
    saveModelSetpoint(saveData, 'MODELSETPOINTSDELETE');
  };

  const handleClose = () => {
    setActive(false);
    setModelSetpointId(0);
  };

  const handleConfirm = () => {
    DeleteModelSetpoint();
    handleClose();
  };

  let messageId = (modelSetpoints && modelSetpoints.displayMessageCode) || 'none';
  const messageText = intl.formatMessage({ id: messageId, defaultMessage: messageId });


  const isUpdateAllowed = IsWidgetAccessible(userProfileWidgets, 'MODELSETPOINTSEDIT');
  const isDeleteAllowed = IsWidgetAccessible(userProfileWidgets, 'MODELSETPOINTSDELETE');

  const dialogTitle = intl.formatMessage({
    id: 'model.confirmDeleteModelSetpoints',
    defaultMessage: 'Are you sure you want to delete the model setpoint?'
  });

  const breadcrumbItems = [
    { label: <FormattedMessage id="modelcategory.modelcategory" defaultMessage="Model Category" />, href: '/operational-setup/model-category-list' },
    { label: <FormattedMessage id="model.models" defaultMessage="Models" />, href: `/operational-setup/model-category-configuration/${ModelCategoryId}/model-list` },
    { label: <FormattedMessage id="model.manageModelSetpoints" defaultMessage="Manage Model Setpoints" /> } // Last item has no href (current page)
  ];

  let isModelCategoryOwner = getModelCategoryPermission(userModelCategories, ModelCategoryId, ModelCategoryPermission.Owner)
  const dataObj = Object.values(filterData());


  return (
    <div className={s.modelSetpoints}>
      {modelSetpoints.isLoading && <LoadingSpinner />}
      {modelSetpoints.showBanner && (
        <Banner
          failureText={messageText}
          showBanner={modelSetpoints.showBanner}
          status={modelSetpoints.isOpSuccessful}
          successText={messageText}
        />
      )}
      <ConfirmDialog title={dialogTitle} onConfirm={handleConfirm} onClose={handleClose} showDialog={active} />
      <ModelConfigurationContentTemplate selectedPage="manageModelSetpoints" userProfileWidgets={userProfileWidgets} intl={intl}>
        <div className={s.contentWrapper}>
          <div className={s.componentHeader}>
            <div className={s.subHeading}>
              <FormattedMessage id="model.manageModelSetpoints" defaultMessage="Manage Model Setpoints" />
              <span className={s.circle}>
                {modelSetpoints.modelSetpointList.length}
              </span>
            </div>
          </div>
          <div className='pb-3'>
            <Breadcrumb items={breadcrumbItems} />
          </div>
          <Row className={s.topRow}>
            <Col sm={6} lg={4}>
              <SearchBar
                searchHandler={searchHandler}
                clearSearchInVisible={false}
                disabled={modelSetpoints.showAddEditScreen}
                placeHolderTextId="model.search"
                data-unittest="searchModelSetpoints"
              />
            </Col>
            <Col sm={6} lg={8}>
              {isModelCategoryOwner && !ReadOnly ? (
                <Button
                  className={s.addbutton}
                  style={WidgetVisibility(userProfileWidgets, 'MODELSETPOINTSADD')}
                  onClick={setAddHandler}
                  disabled={modelSetpoints.showAddEditScreen}
                  data-unittest="addModelSetpointButton"
                >
                  + <FormattedMessage id="model.addNew" defaultMessage="ADD NEW" />
                </Button>
              ) : (
                ''
              )}
            </Col>
          </Row>

          <div>
            <Pagination
              data={sortSetpoints(dataObj)}
              pageSize={DEFAULT_PAGE_SIZE}
              onPageChange={onPageChangeHandler}
              startingPage={modelSetpoints.currentPage}>
              <ModelSetpointsListTable
                setEditHandler={setEditHandler}
                modelCategorySetpoints={modelCategorySetpoints}
                selectedModelSetpointId={currentEditId}
                saveModelSetpoint={saveModelSetpoint}
                modelSetpoints={modelSetpoints}
                describeModelSetpoint={describeModelSetpoint}
                changeModelSetpoint={changeModelSetpoint}
                UpdateAllowed={isUpdateAllowed}
                DeleteAllowed={isDeleteAllowed}
                disableMode={modelSetpoints.showAddEditScreen}
                disableModeHandler={disableModeHandler}
                addNewRef={addNewRef}
                ReadOnly={ReadOnly}
                removeModelSetpoint={removeModelSetpointClick}
                IsModelCategoryOwner={isModelCategoryOwner}
              />
            </Pagination>
          </div>

        </div>
      </ModelConfigurationContentTemplate >
    </div >
  );
};


ModelSetpointsList.propTypes = {
  userProfileWidgets: PropTypes.object.isRequired,
  getModelSetpoints: PropTypes.func.isRequired,
  saveModelSetpoint: PropTypes.func.isRequired,
  describeModelSetpoint: PropTypes.func.isRequired,
  changeModelSetpoint: PropTypes.func.isRequired,
  setModelSetpointShowScreenStatus: PropTypes.func.isRequired
};

export default injectIntl(ModelSetpointsList);
